import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission (replace with actual API call)
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 3000);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <section id="contact" className="py-20 bg-gray-50" data-id="element-433">
      <div className="container" data-id="element-434">
        <div className="text-center mb-16" data-id="element-435">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900" data-id="element-436">
            Get in Touch
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-437">
            Have questions? Our team is here to help. Reach out and we'll
            respond as soon as possible.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto" data-id="element-438">
          {/* Contact Information */}
          <div className="space-y-8" data-id="element-439">
            <div data-id="element-440">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-441">
                Contact Information
              </h3>
              <div className="space-y-6" data-id="element-442">
                <div className="flex items-start gap-4" data-id="element-443">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-444">
                    <Phone size={24} className="text-primary-600" data-id="element-445" />
                  </div>
                  <div data-id="element-446">
                    <p className="font-semibold text-gray-900 mb-1" data-id="element-447">Phone</p>
                    <a href="tel:+254100254254" className="text-gray-600 hover:text-primary-600 transition-colors duration-200" data-id="element-448">
                      +254 100 254 254
                    </a>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-449">
                      Monday - Friday, 8:00 AM - 5:00 PM EAT
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-450">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-451">
                    <Mail size={24} className="text-primary-600" data-id="element-452" />
                  </div>
                  <div data-id="element-453">
                    <p className="font-semibold text-gray-900 mb-1" data-id="element-454">Email</p>
                    <a href="mailto:info@geldhaus.africa" className="text-gray-600 hover:text-primary-600 transition-colors duration-200 block" data-id="element-455">
                      info@geldhaus.africa
                    </a>
                    <a href="mailto:support@geldhaus.africa" className="text-gray-600 hover:text-primary-600 transition-colors duration-200 block" data-id="element-456">
                      support@geldhaus.africa
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-457">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-458">
                    <MapPin size={24} className="text-primary-600" data-id="element-459" />
                  </div>
                  <div data-id="element-460">
                    <p className="font-semibold text-gray-900 mb-1" data-id="element-461">
                      Office Address
                    </p>
                    <p className="text-gray-600" data-id="element-462">
                      Geldhaus Africa
                      <br data-id="element-463" />
                      Nairobi, Kenya
                      <br data-id="element-464" />
                      P.O. Box 12345-00100
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-465">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-466">
                    <Clock size={24} className="text-primary-600" data-id="element-467" />
                  </div>
                  <div data-id="element-468">
                    <p className="font-semibold text-gray-900 mb-1" data-id="element-469">
                      Business Hours
                    </p>
                    <p className="text-gray-600" data-id="element-470">
                      Monday - Friday: 8:00 AM - 5:00 PM
                      <br data-id="element-471" />
                      Saturday: 9:00 AM - 1:00 PM
                      <br data-id="element-472" />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp Contact */}
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6" data-id="element-473">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-474">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24" data-id="element-475">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-476" />
                </svg>
                Quick WhatsApp Support
              </h4>
              <p className="text-gray-700 mb-4 text-sm" data-id="element-477">
                Get instant responses to your questions via WhatsApp
              </p>
              <a href="https://wa.me/254100254254?text=Hello%20Geldhaus,%20I%20would%20like%20to%20inquire%20about%20your%20loan%20products." target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all duration-300" data-id="element-478">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-479">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-480" />
                </svg>
                Chat on WhatsApp
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8" data-id="element-481">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-482">
              Send us a Message
            </h3>

            {isSubmitted ? <div className="flex flex-col items-center justify-center py-12 animate-fadeIn" data-id="element-483">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-484">
                  <CheckCircle size={32} className="text-green-600" data-id="element-485" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2" data-id="element-486">
                  Message Sent!
                </h4>
                <p className="text-gray-600 text-center" data-id="element-487">
                  Thank you for contacting us. We'll get back to you within 24
                  hours.
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-488">
                <div data-id="element-489">
                  <label htmlFor="contact-name" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-490">
                    Full Name *
                  </label>
                  <input type="text" id="contact-name" name="name" value={formData.name} onChange={handleChange} required autoComplete="name" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" placeholder="John Doe" data-id="element-491" />
                </div>

                <div className="grid md:grid-cols-2 gap-6" data-id="element-492">
                  <div data-id="element-493">
                    <label htmlFor="contact-email" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-494">
                      Email Address *
                    </label>
                    <input type="email" id="contact-email" name="email" value={formData.email} onChange={handleChange} required autoComplete="email" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" placeholder="john@example.com" data-id="element-495" />
                  </div>

                  <div data-id="element-496">
                    <label htmlFor="contact-phone" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-497">
                      Phone Number *
                    </label>
                    <input type="tel" id="contact-phone" name="phone" value={formData.phone} onChange={handleChange} required autoComplete="tel" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" placeholder="+254 700 000 000" data-id="element-498" />
                  </div>
                </div>

                <div data-id="element-499">
                  <label htmlFor="contact-subject" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-500">
                    Subject *
                  </label>
                  <select id="contact-subject" name="subject" value={formData.subject} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" data-id="element-501">
                    <option value="" data-id="element-502">Select a subject...</option>
                    <option value="loan-inquiry" data-id="element-503">Loan Inquiry</option>
                    <option value="application-status" data-id="element-504">
                      Application Status
                    </option>
                    <option value="payment" data-id="element-505">Payment Issue</option>
                    <option value="partnership" data-id="element-506">Partnership Opportunity</option>
                    <option value="complaint" data-id="element-507">Complaint</option>
                    <option value="other" data-id="element-508">Other</option>
                  </select>
                </div>

                <div data-id="element-509">
                  <label htmlFor="contact-message" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-510">
                    Message *
                  </label>
                  <textarea id="contact-message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none" placeholder="Tell us how we can help you..." data-id="element-511" />
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-512">
                  {isSubmitting ? <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-513" />
                      Sending...
                    </> : <>
                      <Send size={20} data-id="element-514" />
                      Send Message
                    </>}
                </button>
              </form>}
          </div>
        </div>
      </div>
    </section>;
};